import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridColDef, GridToolbarFilterButton } from '@mui/x-data-grid-premium';
import {
  ConditionalTemplateAnswer_ConditionalTemplateAnswerDataGridPageFilter,
  ConditionalTemplateAnswer_FieldsFragment,
  ConditionalTemplateAnswerDataGridColumns,
  useConditionalTemplateAnswer_GetConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQuery,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionByIdQuery,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import ConditionalTemplateAnswerActions from './components/conditional-template-answer-actions';
import CreateConditionalTemplateAnswerModal from './components/create-conditional-template-answer-modal';
import EditConditionalTemplateAnswerModal from './components/edit-conditional-template-answer-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  button: { marginBottom: '10px' },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  header: {
    marginRight: '10px',
  },
}));

export default function ManageConditionalTemplateAnswers() {
  const classes = useStyles();
  const history = useHistory();

  const { conditionalTemplateQuestionSetId, conditionalTemplateQuestionId } =
    useParams<Record<string, string>>();

  const [selectedConditionalTemplateAnswer, setSelectedConditionalTemplateAnswer] = useState<
    ConditionalTemplateAnswer_FieldsFragment | undefined
  >(undefined);

  const [showCreateConditionalTemplateAnswerModal, setShowCreateConditionalTemplateAnswerModal] =
    useState<boolean>(false);

  const [showEditConditionalTemplateAnswerModal, setShowEditConditionalTemplateAnswerModal] =
    useState<boolean>(false);

  const { data: conditionalTemplateQuestionData, loading: conditionalTemplateQuestionDataLoading } =
    useConditionalTemplateQuestion_GetConditionalTemplateQuestionByIdQuery({
      variables: { id: conditionalTemplateQuestionId },
    });

  const [
    getConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQuery,
    getConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQueryResult,
  ] =
    useConditionalTemplateAnswer_GetConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQuery(
      {
        variables: {
          input: {
            questionId: conditionalTemplateQuestionId,
            paginationInput: {
              page: defaultPaginationModel.page,
              pageSize: defaultPaginationModel.pageSize,
            },
          },
        },
        onError: (e) => {
          showAlert({
            message: 'Error getting conditional template answers: ' + e.message,
            severity: NotificationSeverity.ERROR,
          });
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      },
    );

  const columns: GridColDef<ConditionalTemplateAnswer_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: ConditionalTemplateAnswerDataGridColumns.Answer,
      filterable: true,
      flex: 2,
      headerName: 'Answer',
      renderCell: ({ row }) => {
        return row.answer;
      },
      valueGetter: ({ row }) => row.answer,
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateAnswerDataGridColumns.DocumentGenerationTemplateName,
      filterable: true,
      flex: 2,
      headerName: 'Document Generation Template',
      renderCell: ({ row }) => {
        return row.documentGenerationTemplate?.name || '';
      },
      valueGetter: ({ row }) => row.documentGenerationTemplate?.name || '',
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateAnswerDataGridColumns.ShouldRequestTemplate,
      filterable: true,
      flex: 2,
      headerName: 'Should Request Template',
      renderCell: ({ row }) => {
        return row.shouldRequestTemplate ? 'Yes' : 'No';
      },
    },
    {
      disableColumnMenu: true,
      field: 'actions',
      filterable: false,
      flex: 0.5,
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <ConditionalTemplateAnswerActions
            conditionalTemplateAnswer={row}
            onEditSelectedConditionalTemplateAnswer={() => {
              setSelectedConditionalTemplateAnswer(row);
              setShowEditConditionalTemplateAnswerModal(true);
            }}
          />
        );
      },
    },
  ];

  const getConditionalTemplateAnswersGridToolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  const isLoading =
    conditionalTemplateQuestionDataLoading ||
    getConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQueryResult.loading;
  if (isLoading) {
    return <Spinner />;
  }

  return (
    <HomeLayout showPortalName={false}>
      <>
        {showEditConditionalTemplateAnswerModal && selectedConditionalTemplateAnswer && (
          <EditConditionalTemplateAnswerModal
            conditionalTemplateQuestionId={conditionalTemplateQuestionId}
            showModal={showEditConditionalTemplateAnswerModal}
            setShowModal={setShowEditConditionalTemplateAnswerModal}
            setSelectedConditionalTemplateAnswer={setSelectedConditionalTemplateAnswer}
            selectedConditionalTemplateAnswer={selectedConditionalTemplateAnswer}
          />
        )}
        {showCreateConditionalTemplateAnswerModal && (
          <CreateConditionalTemplateAnswerModal
            conditionalTemplateQuestionId={conditionalTemplateQuestionId}
            showModal={showCreateConditionalTemplateAnswerModal}
            setShowModal={setShowCreateConditionalTemplateAnswerModal}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 2 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>

                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              <span
                className={classes.link}
                onClick={() => {
                  history.push(`/home/manage-conditional-template-question-sets`);
                }}
              >
                Conditional Question Sets
              </span>
              <span className={classes.routeIconContainer}>
                <ChevronRight />
              </span>
              <span
                className={classes.link}
                onClick={() => {
                  history.push(
                    `/home/${conditionalTemplateQuestionSetId}/manage-conditional-template-questions`,
                  );
                }}
              >
                Conditional Questions
              </span>
              <span className={classes.routeIconContainer}>
                <ChevronRight />
              </span>
              Manage Conditional Template Answers
            </Typography>
          </div>
          <Typography variant="body1" style={{ marginTop: '10px' }}>
            {conditionalTemplateQuestionData?.data.question}
          </Typography>
          <div className={classes.button}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginTop: '3px', padding: '2px 10px' }}
              onClick={() => {
                setShowCreateConditionalTemplateAnswerModal(true);
              }}
            >
              + Create New Answer
            </Button>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <ServerEnabledDataGrid<ConditionalTemplateAnswer_ConditionalTemplateAnswerDataGridPageFilter>
                className={classes.root}
                loading={
                  getConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQueryResult.loading
                }
                gridStateKey={GridStateKey.ConditionalTemplateAnswersGrid}
                columns={columns}
                queryResult={
                  getConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQueryResult
                }
                executeLazyQuery={
                  getConditionalTemplateAnswersForConditionalTemplateAnswerDataGridLazyQuery
                }
                disableColumnReorder
                disableRowSelectionOnClick
                autoHeight={true}
                rowHeight={65}
                pagination
                slots={{
                  toolbar: getConditionalTemplateAnswersGridToolbar,
                }}
              />
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
